/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { FC, useEffect, useState } from "react";

const DEFAULT_FADEIN_DELAY = 10;
const DEFAULT_FADEOUT_DELAY = 500;

export interface IFadeInOutProps {
	/**
	 * Function to render conents.
	 * Argument `isVisible` determines if the content has been rendered or is gonna be removed after delay.
	 */
	render: (isVisible: boolean) => JSX.Element;
	/**
	 * If the content should be rendered
	 */
	isVisible: boolean;
	/**
	 * Delay in milliseconds after which the property `isVisible` will be set to true after content is rendered.
	 * Usefull for triggering CSS transitions.
	 */
	fadeInDelay?: number;
	/**
	 * Delay in milliseconds after which the content will be removed.
	 * Should correspond with potential animation delay.
	 * Defaults to 500ms.
	 */
	fadeOutDelay?: number;
}

/**
 * Component to coditionally render content.
 * Provides visiblity flag to trigger CSS transtions.
 *
 * @param props Component props
 */
export const FadeInOut: FC<IFadeInOutProps> = ({ render, isVisible, fadeInDelay, fadeOutDelay }) => {
	const [ visibleFlag, setVisibleFlag ] = useState(false);
	const [ shouldRender, setShouldRender ] = useState(isVisible);

	useEffect(() => {
		let t;

		const _fadeInDelay = fadeInDelay !== undefined ? fadeInDelay : DEFAULT_FADEIN_DELAY;
		const _fadeOutDelay = fadeOutDelay !== undefined ? fadeOutDelay : DEFAULT_FADEOUT_DELAY;

		if (isVisible) {
			setShouldRender(true);
			setTimeout(() => setVisibleFlag(true), _fadeInDelay);
		} else {
			setVisibleFlag(false);
			setTimeout(() => setShouldRender(false), _fadeOutDelay);
		}

		return () => clearTimeout(t);
	}, [ isVisible, fadeInDelay, fadeOutDelay ]);

	if (shouldRender) {
		return render(visibleFlag);
	} else {
		return null;
	}
};
